import {
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    IconButton,
    Paper,
    Stack,
    Skeleton,
    Tooltip,
    Typography,
} from "@mui/material"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined"
import { unwrapResult } from "@reduxjs/toolkit"
import { useEffect, useState } from "react"
import { DeleteIconButton } from "../../../app/components/iconButtons/DeleteIconButton"
import { EditIconButton } from "../../../app/components/iconButtons/EditIconButton"
import { useAppDispatch } from "../../../app/hooks"
import { StructureNodeTreeDto } from "../../structures/structureSlice"
import { DocumentWorkflowStageTypeEnum } from "../../documents/workflow/documentWorkflowApi"
import { showError, showSuccess } from "../../notifications/notificationsSlice"
import { AddCompanyWorkflowDefinitionDialog } from "./AddCompanyWorkflowDefinitionDialog"
import { AddEditCompanyWorkflowStageDialog } from "./AddEditCompanyWorkflowStageDialog"
import {
    deleteCompanyWorkflowDefinitionStage,
    DocumentWorkflowDefinitionDetailsDto,
    DocumentWorkflowDefinitionStageDetailsDto,
    loadCompanyWorkflowDefinitionsForOrganizationNode,
} from "./companyDocumentWorkflowSlice"

interface CompanyDocumentWorkflowPanelProps {
    node: StructureNodeTreeDto | null
    /** Rendered inside another panel (a tab): no own Paper and no node header. */
    embedded?: boolean
}

const normalizeWorkflowStageType = (stageType: unknown): "EDIT" | "REVIEW" | "APPROVAL" => {
    if (stageType === "EDIT" || stageType === "REVIEW" || stageType === "APPROVAL") {
        return stageType
    }

    return "REVIEW"
}

export const CompanyDocumentWorkflowPanel = ({ node, embedded = false }: CompanyDocumentWorkflowPanelProps) => {
    const dispatch = useAppDispatch()
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [definitions, setDefinitions] = useState<DocumentWorkflowDefinitionDetailsDto[]>([])
    const [reloadTick, setReloadTick] = useState(0)

    const [addDialogOpen, setAddDialogOpen] = useState(false)
    const [editDefinitionDialogOpen, setEditDefinitionDialogOpen] = useState(false)
    const [definitionToEdit, setDefinitionToEdit] = useState<DocumentWorkflowDefinitionDetailsDto | null>(null)
    const [isSavingStage, setIsSavingStage] = useState(false)
    const [addStageDialogOpen, setAddStageDialogOpen] = useState(false)
    const [addStageDefinition, setAddStageDefinition] = useState<DocumentWorkflowDefinitionDetailsDto | null>(null)
    const [addStageType, setAddStageType] = useState<DocumentWorkflowStageTypeEnum>("REVIEW")
    const [editDialogOpen, setEditDialogOpen] = useState(false)
    const [editStageDefinition, setEditStageDefinition] = useState<DocumentWorkflowDefinitionDetailsDto | null>(null)
    const [editingStage, setEditingStage] = useState<DocumentWorkflowDefinitionStageDetailsDto | null>(null)
    const [stageActionError, setStageActionError] = useState<string | null>(null)

    useEffect(() => {
        if (!node) {
            setDefinitions([])
            setError(null)
            setIsLoading(false)
            return
        }

        const selectedNode = node

        let active = true
        setIsLoading(true)
        setError(null)

        dispatch(loadCompanyWorkflowDefinitionsForOrganizationNode({ organizationNodeId: selectedNode.id }))
            .then(unwrapResult)
            .then(data => {
                if (!active) {
                    return
                }

                setDefinitions(data)
            })
            .catch(() => {
                if (!active) {
                    return
                }

                setDefinitions([])
                setError("Nie udało się załadować workflow dla wybranego noda.")
            })
            .finally(() => {
                if (active) {
                    setIsLoading(false)
                }
            })

        return () => {
            active = false
        }
    }, [dispatch, node, reloadTick])

    const handleOpenAddDialog = () => {
        setAddDialogOpen(true)
    }

    const handleCloseAddDialog = () => {
        setAddDialogOpen(false)
    }

    const handleOpenEditDefinitionDialog = (definition: DocumentWorkflowDefinitionDetailsDto) => {
        setDefinitionToEdit(definition)
        setEditDefinitionDialogOpen(true)
    }

    const handleCloseEditDefinitionDialog = () => {
        setEditDefinitionDialogOpen(false)
        setDefinitionToEdit(null)
    }

    const buildStageServerError = (payload: unknown, fallback: string) => {
        if (!payload || typeof payload !== "object") {
            return fallback
        }

        const apiPayload = payload as {
            kind?: string
            problem?: {
                title?: string
                detail?: string
                errors?: Record<string, unknown>
                problems?: unknown[]
            }
        }

        if (apiPayload.kind !== "http" || !apiPayload.problem) {
            return fallback
        }

        const problem = apiPayload.problem

        const baseMessage = [problem.title, problem.detail].filter(Boolean).join(": ")

        const validationMessages: string[] = []

        if (problem.errors && typeof problem.errors === "object") {
            Object.entries(problem.errors).forEach(([field, value]) => {
                if (Array.isArray(value)) {
                    value.forEach(item => {
                        if (typeof item === "string" && item.trim()) {
                            validationMessages.push(`${field}: ${item}`)
                        }
                    })
                } else if (typeof value === "string" && value.trim()) {
                    validationMessages.push(`${field}: ${value}`)
                }
            })
        }

        if (problem.problems && Array.isArray(problem.problems)) {
            problem.problems.forEach(item => {
                if (typeof item === "string" && item.trim()) {
                    validationMessages.push(item)
                }
            })
        }

        if (validationMessages.length === 0) {
            return baseMessage || fallback
        }

        return `${baseMessage || fallback}\n${validationMessages.join("\n")}`
    }

    const handleOpenAddStageDialog = (definition: DocumentWorkflowDefinitionDetailsDto, type: DocumentWorkflowStageTypeEnum) => {
        setEditDialogOpen(false)
        setEditStageDefinition(null)
        setEditingStage(null)
        setAddStageDefinition(definition)
        setAddStageType(type)
        setStageActionError(null)
        setAddStageDialogOpen(true)
    }

    const handleCloseAddStageDialog = () => {
        if (!isSavingStage) {
            setAddStageDialogOpen(false)
            setAddStageDefinition(null)
        }
    }

    const handleOpenEditStageDialog = (definition: DocumentWorkflowDefinitionDetailsDto, stage: DocumentWorkflowDefinitionStageDetailsDto) => {
        setAddStageDialogOpen(false)
        setAddStageDefinition(null)
        setEditStageDefinition(definition)
        setEditingStage(stage)
        setStageActionError(null)
        setEditDialogOpen(true)
    }

    const handleCloseEditStageDialog = () => {
        if (!isSavingStage) {
            setEditDialogOpen(false)
            setEditStageDefinition(null)
            setEditingStage(null)
        }
    }

    const handleCloseStageDialog = () => {
        if (!isSavingStage) {
            setAddStageDialogOpen(false)
            setAddStageDefinition(null)
            setEditDialogOpen(false)
            setEditStageDefinition(null)
            setEditingStage(null)
        }
    }

    const handleDeleteStage = async (definitionId: number, versionId: number, stageId: number) => {
        setIsSavingStage(true)
        setStageActionError(null)

        try {
            const result = await dispatch(deleteCompanyWorkflowDefinitionStage({ definitionId, versionId, stageId }))

            if (deleteCompanyWorkflowDefinitionStage.fulfilled.match(result)) {
                dispatch(showSuccess("saved"))
                setReloadTick(prev => prev + 1)
            } else {
                setStageActionError(buildStageServerError(result.payload, "Nie udało się usunąć etapu workflow."))
            }
        } catch {
            dispatch(showError("error"))
        } finally {
            setIsSavingStage(false)
        }
    }

    if (!node) {
        return (
            <Paper sx={{ p: 2, alignSelf: "start", borderStyle: "dashed", borderWidth: 1, borderColor: "divider" }}>
                <Alert severity="info">Kliknij node w drzewku po lewej stronie, a tutaj pokaże się panel workflow.</Alert>
            </Paper>
        )
    }

    return (
        <Box
            component={embedded ? "div" : Paper}
            sx={embedded ? undefined : { p: 2, position: { md: "sticky" }, top: { md: 88 }, alignSelf: "start" }}
        >
            <Stack spacing={2}>
                {!embedded && (
                    <>
                        <Box>
                            <Typography variant="overline" color="text.secondary">
                                Definicje workflow dla elementu struktury
                            </Typography>

                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {node.code ? `${node.code} – ${node.name}` : node.name}
                            </Typography>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.5 }}
                            >
                                Zarządzaj definicjami workflow i etapami przypisanymi do tego
                                elementu struktury.
                            </Typography>
                        </Box>

                        <Divider />
                    </>
                )}

                <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="subtitle2">
                            Definicje workflow
                        </Typography>
                        <Button variant="contained" size="small" onClick={handleOpenAddDialog}>
                            Dodaj
                        </Button>
                    </Stack>

                    {isLoading && (
                        <Stack spacing={1}>
                            <Skeleton variant="rounded" height={84} />
                            <Skeleton variant="rounded" height={84} />
                        </Stack>
                    )}

                    {!isLoading && error && <Alert severity="error">{error}</Alert>}

                    {!isLoading && !error && definitions.length === 0 && (
                        <Alert severity="info">Brak definicji workflow dla wybranego noda.</Alert>
                    )}

                    {!isLoading && !error && definitions.length > 0 && (
                        <Stack spacing={1.5}>
                            {definitions.map(definition => (
                                <Paper key={definition.id} variant="outlined" sx={{ p: 1.25 }}>
                                    <Stack spacing={1}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                            <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                                {definition.name}
                                            </Typography>
                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                <Chip
                                                    size="small"
                                                    label={definition.isActive ? "Aktywna" : "Nieaktywna"}
                                                    color={definition.isActive ? "success" : "default"}
                                                    variant={definition.isActive ? "filled" : "outlined"}
                                                />
                                                <EditIconButton
                                                    tooltip="Edytuj definicję"
                                                    disabled={isSavingStage}
                                                    onClick={() => handleOpenEditDefinitionDialog(definition)}
                                                />
                                            </Stack>
                                        </Stack>

                                        <Typography variant="caption" color="text.secondary">
                                            {`Wersja: ${definition.versionNumber} | Zmiana: ${definition.changeDescription ?? "-"}`}
                                        </Typography>

                                        {definition.description && (
                                            <Typography variant="body2" color="text.secondary">
                                                {definition.description}
                                            </Typography>
                                        )}

                                        <Divider />

                                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                            <Typography variant="subtitle2">Etapy</Typography>
                                            <Stack direction="row" spacing={0.5}>
                                                <Tooltip title="Dodaj etap Review">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            disabled={isSavingStage}
                                                            onClick={() => handleOpenAddStageDialog(definition, "REVIEW")}
                                                        >
                                                            <RateReviewOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Dodaj etap Approval">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            color="success"
                                                            disabled={isSavingStage}
                                                            onClick={() => handleOpenAddStageDialog(definition, "APPROVAL")}
                                                        >
                                                            <CheckCircleOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Stack>
                                        </Stack>

                                        {stageActionError && (
                                            <Alert severity="error">{stageActionError}</Alert>
                                        )}

                                        <Stack spacing={0.75}>
                                            {definition.stages
                                                .slice()
                                                .sort((a, b) => a.order - b.order)
                                                .map(stage => (
                                                    <Paper key={stage.id} variant="outlined" sx={{ p: 1 }}>
                                                        <Stack spacing={0.5}>
                                                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                    <Chip size="small" label={`#${stage.order}`} />
                                                                    <Chip size="small" label={String(stage.type)} color="info" variant="outlined" />
                                                                </Stack>
                                                                <Stack direction="row" spacing={0.5}>
                                                                    <EditIconButton
                                                                        tooltip="Edytuj etap"
                                                                        disabled={isSavingStage}
                                                                        onClick={() => handleOpenEditStageDialog(definition, stage)}
                                                                    />
                                                                    <DeleteIconButton
                                                                        tooltip="Usuń etap"
                                                                        disabled={isSavingStage}
                                                                        onClick={() => { void handleDeleteStage(definition.id, definition.versionId, stage.id) }}
                                                                    />
                                                                </Stack>
                                                            </Stack>
                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                {stage.name}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {stage.description || "Brak opisu"}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {`Deadline [dni]: ${stage.defaultDeadlineDays ?? "-"}`}
                                                            </Typography>
                                                        </Stack>
                                                    </Paper>
                                                ))}
                                        </Stack>
                                    </Stack>
                                </Paper>
                            ))}
                        </Stack>
                    )}
                </Box>
            </Stack>

            <AddCompanyWorkflowDefinitionDialog
                open={addDialogOpen}
                node={node}
                mode="add"
                onClose={handleCloseAddDialog}
                onCreated={definition => {
                    setAddDialogOpen(false)
                    setDefinitions(prev => [definition, ...prev])
                }}
            />

            <AddCompanyWorkflowDefinitionDialog
                open={editDefinitionDialogOpen}
                node={node}
                mode="edit"
                definitionToEdit={definitionToEdit}
                onClose={handleCloseEditDefinitionDialog}
                onCreated={() => { }}
                onUpdated={definition => {
                    setDefinitions(prev => prev.map(item => item.id === definition.id ? definition : item))
                    setEditDefinitionDialogOpen(false)
                    setDefinitionToEdit(null)
                }}
            />

            <AddEditCompanyWorkflowStageDialog
                open={addStageDialogOpen || editDialogOpen}
                mode={editDialogOpen ? "edit" : "add"}
                definition={editDialogOpen ? editStageDefinition : addStageDefinition}
                stageType={addStageType}
                stage={editDialogOpen ? editingStage : null}
                onClose={handleCloseStageDialog}
                onSaved={definition => {
                    setDefinitions(prev => prev.map(item => item.id === definition.id ? definition : item))
                    setAddStageDialogOpen(false)
                    setAddStageDefinition(null)
                    setEditDialogOpen(false)
                    setEditStageDefinition(null)
                    setEditingStage(null)
                }}
            />
        </Box>
    )
}
